/**
 * utils/clientDisplay.ts
 *
 * Centralised display helpers for client records.
 * Handles both 'individual' and 'company' client types, providing a single
 * source of truth for name display, subtitle, initials, and type detection.
 * Created Apr 2026: introduced to support dual individual/company client model.
 */

import type { Database } from '../types/database';

type Client = Pick<
  Database['public']['Tables']['clients']['Row'],
  | 'client_type'
  | 'first_name'
  | 'last_name'
  | 'name'
  | 'email'
  | 'company_name'
  | 'company_registration_number'
>;

export function isCompany(client: Pick<Client, 'client_type'>): boolean {
  return client.client_type === 'company';
}

/**
 * Returns the primary display name for a client.
 * - Company: company_name
 * - Individual: "last_name first_name", falling back to name or email
 */
export function getClientDisplayName(client: Client): string | null {
  if (isCompany(client)) {
    return client.company_name || null;
  }
  const parts = [client.last_name, client.first_name].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  return client.name || client.email || null;
}

/**
 * Returns a secondary line of contextual info below the primary name.
 * - Company: contact person (first_name + last_name) if set, else email
 * - Individual: email
 */
export function getClientSubtitle(client: Client): string | null {
  if (isCompany(client)) {
    const contact = [client.first_name, client.last_name].filter(Boolean).join(' ');
    return contact || client.email || null;
  }
  return client.email || null;
}

/**
 * Returns 1-2 uppercase initials for use in the avatar.
 * - Company: up to 2 meaningful characters from company_name
 * - Individual: first letter of last_name + first letter of first_name
 */
export function getClientInitials(client: Client): string {
  if (isCompany(client)) {
    const name = client.company_name;
    if (!name) return '?';
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 1) return words[0][0]?.toUpperCase() ?? '?';
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  const last = client.last_name;
  const first = client.first_name;
  if (last && first) return (last[0] + first[0]).toUpperCase();
  if (last) return last[0].toUpperCase();
  if (first) return first[0].toUpperCase();
  if (client.name) {
    const parts = client.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  }
  return '?';
}
